import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { SwatchIcon, XIcon, ChevronDownIcon } from './Icons';

/**
 * ColorInput Component - A color picker with consistent styling
 * 
 * Features:
 * - Visual color picker with swatches
 * - Manual hex input
 * - Named color support
 * - Color preview
 * - Validation
 * - Matches Input component styling
 */
const ColorInput = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Enter color (e.g., #FF0000 or red)',
  disabled = false,
  error = null,
  required = false,
  size = 'default',
  className = '',
  ...props
}) => {
  const { theme } = useTheme();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const pickerRef = useRef(null);
  const inputRef = useRef(null);

  // Size variants to match Input component
  const sizeClasses = {
    sm: 'h-8 px-2 py-1 text-xs',
    default: 'h-10 px-3 py-2 text-sm',
    lg: 'h-12 px-4 py-3 text-base'
  };

  const colorPreviewSizes = {
    sm: 'w-6 h-6 m-1',
    default: 'w-8 h-8 m-1', 
    lg: 'w-10 h-10 m-1'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    default: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Update internal state when value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };

    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isPickerOpen]);

  // Predefined color swatches
  const colorSwatches = [
    // Primary colors
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#A855F7' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Gray', value: '#6B7280' },
    
    // Status colors
    { name: 'Success', value: '#10B981' },
    { name: 'Warning', value: '#F59E0B' },
    { name: 'Error', value: '#EF4444' },
    { name: 'Info', value: '#3B82F6' },
    
    // Document status colors
    { name: 'Draft', value: '#94A3B8' },
    { name: 'Review', value: '#F59E0B' },
    { name: 'Approved', value: '#10B981' },
    { name: 'Published', value: '#3B82F6' },
    { name: 'Deprecated', value: '#EF4444' },
    { name: 'Archived', value: '#6B7280' },
  ];

  // Validate color format
  const isValidColor = (color) => {
    if (!color) return true; // Empty is valid if not required
    
    // Hex color pattern
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexPattern.test(color)) return true;
    
    // Named colors (basic set)
    const namedColors = [
      'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'brown',
      'black', 'white', 'gray', 'grey', 'cyan', 'magenta', 'lime', 'navy',
      'maroon', 'olive', 'teal', 'silver', 'gold'
    ];
    
    return namedColors.includes(color.toLowerCase());
  };

  // Get display color (convert named colors to hex for preview)
  const getDisplayColor = (color) => {
    if (!color) return '#CCCCCC';
    
    // If it's already a hex color, return it
    if (color.startsWith('#')) return color;
    
    // Convert common named colors to hex for preview
    const namedColorMap = {
      red: '#FF0000', green: '#008000', blue: '#0000FF', yellow: '#FFFF00',
      orange: '#FFA500', purple: '#800080', pink: '#FFC0CB', brown: '#A52A2A',
      black: '#000000', white: '#FFFFFF', gray: '#808080', grey: '#808080',
      cyan: '#00FFFF', magenta: '#FF00FF', lime: '#00FF00', navy: '#000080',
      maroon: '#800000', olive: '#808000', teal: '#008080', silver: '#C0C0C0',
      gold: '#FFD700'
    };
    
    return namedColorMap[color.toLowerCase()] || color;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (isValidColor(newValue)) {
      onChange?.(newValue);
    }
  };

  // Handle color swatch selection
  const handleSwatchSelect = (color) => {
    setInputValue(color);
    onChange?.(color);
    setIsPickerOpen(false);
  };

  // Handle input blur
  const handleBlur = (e) => {
    onBlur?.(e);
    
    // Validate and clean up the input
    if (inputValue && !isValidColor(inputValue)) {
      setInputValue(value); // Reset to last valid value
    }
  };

  // Clear color
  const handleClear = () => {
    setInputValue('');
    onChange?.('');
  };

  const displayColor = getDisplayColor(inputValue);
  const hasValidColor = inputValue && isValidColor(inputValue);

  // Base styling to match Input component
  const baseClasses = `
    flex items-center w-full rounded-lg border
    transition-all duration-200 ease-in-out
    focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500/20
    disabled:cursor-not-allowed disabled:opacity-50
    backdrop-blur-sm hover:shadow-sm
  `;

  const getThemeClasses = () => {
    if (error) {
      return `${theme.colors.surface} ${theme.text.primary} border-red-500 focus-within:border-red-500`;
    }
    return `${theme.colors.surface} ${theme.text.primary} ${theme.border.light} focus-within:${theme.border.light}`;
  };

  return (
    <div className={cn('relative', className)}>
      {/* Main input container */}
      <div className="relative">
        <div
          className={cn(
            baseClasses,
            sizeClasses[size],
            getThemeClasses(),
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {/* Color preview square */}
          <div 
            className={cn(
              'flex-shrink-0 rounded border border-gray-300 dark:border-gray-600 cursor-pointer transition-all',
              colorPreviewSizes[size],
              disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-400 dark:hover:border-gray-500'
            )}
            style={{ backgroundColor: hasValidColor ? displayColor : '#f3f4f6' }}
            onClick={() => !disabled && setIsPickerOpen(!isPickerOpen)}
            title={hasValidColor ? `Color: ${inputValue}` : 'Click to open color picker'}
          >
            {!hasValidColor && (
              <div className="w-full h-full flex items-center justify-center">
                <SwatchIcon className={cn(iconSizes[size], "text-gray-400")} />
              </div>
            )}
          </div>

          {/* Text input */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="flex-1 bg-transparent border-0 outline-none placeholder-gray-400 dark:placeholder-gray-500 disabled:cursor-not-allowed"
            {...props}
          />

          {/* Clear button */}
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              title="Clear color"
            >
              <XIcon className={cn(iconSizes[size])} />
            </button>
          )}

          {/* Picker toggle button */}
          <button
            type="button"
            onClick={() => !disabled && setIsPickerOpen(!isPickerOpen)}
            disabled={disabled}
            className={cn(
              'flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            title="Open color picker"
          >
            <ChevronDownIcon className={cn(iconSizes[size])} />
          </button>
        </div>

        {/* Color picker dropdown */}
        {isPickerOpen && !disabled && (
          <div
            ref={pickerRef}
            className={cn(
              'absolute top-full left-0 right-0 z-50 mt-1 p-4 rounded-lg border shadow-lg',
              'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            )}
          >
            <div className="space-y-3">
              {/* Section title */}
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Choose a color
              </div>

              {/* Color swatches grid */}
              <div className="grid grid-cols-8 gap-2">
                {colorSwatches.map((swatch, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSwatchSelect(swatch.value)}
                    className={cn(
                      'w-8 h-8 rounded border-2 transition-all hover:scale-110',
                      inputValue === swatch.value 
                        ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    )}
                    style={{ backgroundColor: swatch.value }}
                    title={`${swatch.name} (${swatch.value})`}
                  />
                ))}
              </div>

              {/* Manual hex input */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Or enter hex code:
                </label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  placeholder="#FF0000"
                  className={cn(
                    'w-full px-2 py-1 text-sm rounded border',
                    'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500',
                    'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* Validation hint */}
      {inputValue && !isValidColor(inputValue) && !error && (
        <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
          Please enter a valid hex color (e.g., #FF0000) or color name (e.g., red)
        </p>
      )}
    </div>
  );
};

export default ColorInput;
